import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { MaskedInput, Select, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import {
  useCreateInsurerMutation,
  useGetInsurersQuery,
  usePatchInsurerByIdMutation,
} from "services/insurer-api.ts";
import type { InsurerType } from "types/DataTypes";
import { useConstants } from "utils/hooks";

type InsurerInformationFormProps = {
  fileFromServiceId?: string;
};

const InsurerInformationForm: React.FC<
  InsurerInformationFormProps & { isReadOnly?: boolean }
> = ({ fileFromServiceId, isReadOnly = false }) => {
  const { constants } = useConstants();
  const { fileId: fileIdFromParams } = useParams<{ fileId: string }>();
  const [patchInsurerById] = usePatchInsurerByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [createInsurer] = useCreateInsurerMutation();

  const [selectedInsurerId, setSelectedInsurerId] = useState<string | null>(
    null,
  );
  const prevSelectedInsurerIdRef = useRef<string | null>(null);

  const [insurerOptions, setInsurerOptions] = useState<OptionType[]>([]);
  const [insurerSearchTerm, setInsurerSearchTerm] = useState<string | null>(
    null,
  );

  const debouncedSetInsurerSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setInsurerSearchTerm(value);
      }, 500),
    [],
  );

  // cancel any pending debounced search on unmount so it can't fire setState
  // after the component is gone
  useEffect(
    () => () => debouncedSetInsurerSearchTerm.cancel(),
    [debouncedSetInsurerSearchTerm],
  );

  const fileId = fileFromServiceId ?? fileIdFromParams;

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  const { data: insurers, isFetching: isInsurersFetching } =
    useGetInsurersQuery(
      { searchTerm: insurerSearchTerm ?? undefined },
      { skip: !insurerSearchTerm },
    );

  useEffect(() => {
    if (insurers) {
      setInsurerOptions(
        insurers.results?.map((insurer: InsurerType) => ({
          label: insurer.name,
          value: insurer.id.toString(),
        })) ?? [],
      );
    }
  }, [insurers]);

  // keep selectedInsurerId in sync with the file, including when the insurer
  // is unassigned (fileData.insurer === null) so the detail fields lock back
  // down
  useEffect(() => {
    setSelectedInsurerId(fileData?.insurer?.id?.toString() ?? null);
  }, [fileData]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (!selectedInsurerId) return;
      await patchInsurerById({
        id: selectedInsurerId,
        data: {
          [field]: value,
        },
      });
    },
    [patchInsurerById, selectedInsurerId],
  );

  const handleSaveFile = useCallback(
    async (field: string, value: string | number | null) => {
      await patchFileById({
        id: fileId,
        data: {
          [field]: value,
        },
      });
    },
    [patchFileById, fileId],
  );

  const handleChangeInsurerOnFile = useCallback(
    async (o: OptionType | null) => {
      if (isReadOnly) return;
      await handleSaveFile("insurer_id", o?.value?.toString() ?? null);
      setSelectedInsurerId(o?.value?.toString() ?? null);
    },
    [handleSaveFile, isReadOnly],
  );

  const handleCreateAndAssignInsurer = useCallback(
    async (insurerName: string) => {
      if (!fileId) return;

      try {
        const result = await createInsurer({
          data: { name: insurerName },
        }).unwrap();

        if (result.id) {
          // automatically assign the newly created insurer to the file
          // (routed through handleSaveFile's computed key so the file patch
          // stays consistent with the insurer_id assignment used elsewhere)
          await handleSaveFile("insurer_id", result.id.toString());
          setSelectedInsurerId(result.id.toString());

          // both mutations have resolved, so reset the search state
          // synchronously: clearing the term/options re-renders the Select
          // with no active "Add" option, closing the dropdown naturally
          setInsurerSearchTerm(null);
          setInsurerOptions([]);
          if (document.activeElement) {
            (document.activeElement as HTMLElement).blur();
          }
        }
      } catch (err) {
        console.error("Failed to create and assign insurer:", err);
        KaboodleToaster.error("Failed to create insurer. Please try again.");
      }
    },
    [createInsurer, handleSaveFile, fileId],
  );

  const {
    control,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!fileData?.insurer) return;
    const currentInsurerId = fileData.insurer.id.toString();
    const isSwitchingInsurers =
      prevSelectedInsurerIdRef.current !== currentInsurerId;
    prevSelectedInsurerIdRef.current = currentInsurerId;

    reset(fileData.insurer, {
      keepDirtyValues: !isSwitchingInsurers,
    });
  }, [fileData, reset]);

  const selectedInsurerValue = useMemo(
    () =>
      fileData?.insurer
        ? {
            label: fileData.insurer.name,
            value: fileData.insurer.id.toString(),
          }
        : null,
    [fileData?.insurer],
  );

  return (
    <Card
      heading="Insurer Information"
      classNames={fileFromServiceId ? "future-medical-insurer-information" : ""}
    >
      <Select
        name="insurer"
        label="Insurer"
        options={insurerOptions}
        value={selectedInsurerValue}
        placeholder="Search for an insurer..."
        readOnly={isReadOnly}
        // preserves the prior behavior: a file always keeps its assigned
        // insurer until a different one is picked (unassigning is not a
        // supported action here); handleChangeInsurerOnFile still handles
        // null defensively
        isClearable={false}
        onChange={(o: OptionType | null) => handleChangeInsurerOnFile(o)}
        onInputChange={(inputValue: string) =>
          !isReadOnly && debouncedSetInsurerSearchTerm(inputValue)
        }
        noOptionsMessage={({ inputValue }: { inputValue: string }) => {
          if (!inputValue) return "Begin typing to search for insurers...";
          if (isInsurersFetching) return "Searching...";

          // only show "Add new item" if we have searched and found no results
          if (inputValue && insurerSearchTerm && insurerOptions.length === 0) {
            return (
              <div className="custom-no-options">
                <div className="no-options-text">No insurers found.</div>
                <button
                  type="button"
                  className="add-new-item-btn"
                  onClick={() => handleCreateAndAssignInsurer(inputValue)}
                >
                  Add {inputValue}
                </button>
              </div>
            );
          }

          return "No insurers found.";
        }}
      />
      <ControlledInput
        control={control}
        name="name"
        label="Insurer Name"
        onSave={(value) => handleSave("name", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedInsurerId}
      />
      <ControlledInput
        control={control}
        name="address"
        label="Insurer Address"
        onSave={(value) => handleSave("address", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={isReadOnly || !selectedInsurerId}
      />
      <ControlledSelect
        control={control}
        label="State"
        name="state"
        options={constants?.STATES || []}
        onSave={(o: OptionType) => handleSave("state", o.value?.toString())}
        errors={errors}
        preventClear={true}
        readOnly={isReadOnly || !selectedInsurerId}
      />
      <ControlledInput
        control={control}
        name="zipcode"
        label="Zip Code"
        onSave={(value) => handleSave("zipcode", value)}
        errors={errors}
        componentType={MaskedInput}
        mask="zipcode"
        readOnly={isReadOnly || !selectedInsurerId}
      />
    </Card>
  );
};

export default InsurerInformationForm;
